// Visible rank tiers (Ladder K, designed with Mathias 2026-08-19): a
// Bronze→Diamond climb with I/II/III sub-tiers, then two single apex tiers,
// Master and Wormlord. Casual shows only the tier (the number stays hidden);
// Ranked will reveal the number too.
//
// Thresholds are on the displayRating scale (~1000 = brand new, ~2400 = elite)
// and are PLACEHOLDERS — retune against the real distribution so most players
// land mid (Gold) once we have data.
import { PlayerRating, displayRating } from "./rating";

export interface Tier {
  id: string;
  name: string;
  color: string; // accent for the badge dot/label
  min: number; // inclusive lower bound on displayRating
}

// Centered on Gold ≈ 1000 (the average). Placeholders — retune against the real
// distribution so the bands match where players actually land.
// Every boundary is a round number: tiers are 300 wide, so each sub-tier steps
// at exactly +100 (Bronze 400 / 500 / 600, Silver 700..., Gold 1000...). A brand
// new player (rating 1000) starts at the bottom of Gold; anything under 400 is
// still Bronze I. RATING_SCALE (rating.ts) is tuned wide so these are reachable.
export const TIERS: Tier[] = [
  { id: "bronze", name: "Bronze", color: "#cd7f32", min: 400 },
  { id: "silver", name: "Silver", color: "#c3ccd9", min: 700 },
  { id: "gold", name: "Gold", color: "#ffcf4d", min: 1000 },
  { id: "platinum", name: "Platinum", color: "#3fe0c8", min: 1300 },
  { id: "diamond", name: "Diamond", color: "#5aa9ff", min: 1600 },
  { id: "master", name: "Master", color: "#ff2e88", min: 1900 },
  { id: "wormlord", name: "Wormlord", color: "#b07cff", min: 2200 },
];
// The lowest display rating there is (rating.ts displayRating). Bronze I runs
// from here, not from Bronze's 400: "anything under 400 is still Bronze I", so
// its band must too, or a climb from 371 to 457 crossed an invisible 400 edge
// and played a promotion from Bronze I to Bronze I.
const LADDER_FLOOR = 100;
// The top tiers with NO sub-tiers: the ladder ends ...Diamond III → Master → Wormlord.
const SINGLE_TIERS = 2;

export function tierForValue(value: number): Tier {
  let match = TIERS[0];
  for (const t of TIERS) if (value >= t.min) match = t;
  return match;
}

// Sub-tier (I/II/III) within a tier: each tier's rating span is split into
// three equal bands. Master and Wormlord are single tiers — no numerals.
// Label reads "Gold II" / "Master" / "Wormlord".
export interface TierInsignia {
  tier: Tier;
  tierIndex: number;
  numeral: "I" | "II" | "III" | null;
  label: string;
}
const NUMERALS = ["I", "II", "III"] as const;

// Every visual step of the ladder as a representative displayRating (three per
// sub-tiered tier, one per apex tier), Bronze I first. Dev/preview aid: powers
// /debug/ranks and the ?ranksdemo flags on the lobby and leaderboard.
export function ladderSteps(): number[] {
  const steps: number[] = [];
  TIERS.forEach((t, i) => {
    if (i >= TIERS.length - SINGLE_TIERS) {
      steps.push(t.min + 50);
      return;
    }
    const span = TIERS[i + 1].min - t.min;
    for (let s = 0; s < 3; s++) steps.push(Math.round(t.min + (span * (s + 0.5)) / 3));
  });
  return steps;
}

export function insigniaForValue(value: number): TierInsignia {
  const tier = tierForValue(value);
  const idx = TIERS.indexOf(tier);
  if (idx >= TIERS.length - SINGLE_TIERS) return { tier, tierIndex: idx, numeral: null, label: tier.name };
  const span = TIERS[idx + 1].min - tier.min;
  const sub = Math.min(2, Math.max(0, Math.floor(((value - tier.min) / span) * 3)));
  return { tier, tierIndex: idx, numeral: NUMERALS[sub], label: `${tier.name} ${NUMERALS[sub]}` };
}

export function tierFor(r: PlayerRating): Tier {
  return tierForValue(displayRating(r));
}

// Progress within the current sub-tier band toward the next boundary (drives the
// rating bar + "x left" caption on the Match End modal and the results card).
// Returns null at the apex (Wormlord: nothing above).
export function subProgress(value: number): { pct: number; toNext: number; nextLabel: string } | null {
  const idx = TIERS.indexOf(tierForValue(value));
  if (idx >= TIERS.length - SINGLE_TIERS) {
    const next = idx === TIERS.length - SINGLE_TIERS ? TIERS[TIERS.length - 1] : null;
    if (!next) return null;
    const span = next.min - TIERS[idx].min;
    return { pct: Math.min(1, (value - TIERS[idx].min) / span), toNext: next.min - value, nextLabel: next.name };
  }
  const tier = TIERS[idx];
  const span = (TIERS[idx + 1].min - tier.min) / 3;
  const sub = Math.max(0, Math.min(2, Math.floor((value - tier.min) / span)));
  const bound = tier.min + span * (sub + 1);
  const low = idx === 0 && sub === 0 ? LADDER_FLOOR : tier.min + span * sub; // Bronze I starts at the floor
  const nextLabel = sub === 2 ? TIERS[idx + 1].name + " I" : `${tier.name} ${NUMERALS[sub + 1]}`;
  return { pct: Math.max(0, (value - low) / (bound - low)), toNext: Math.round(bound - value), nextLabel };
}

// The sub-tier band a value sits in: the rating bar's coordinate system, in
// the match-end modal and the results card alike (each tier splits into three
// sub-bands, I / II / III; the top two tiers are one band each).
// `apex` marks the top tier's band: it has no edge above it (its width is
// nominal, for the bar), so a walk up the ladder must stop there rather than
// look for the next edge. A win landing above 2500 used to walk forever.
export function bandOf(value: number): { low: number; width: number; apex?: boolean } {
  const idx = TIERS.indexOf(insigniaForValue(value).tier);
  if (idx >= TIERS.length - 1) return { low: TIERS[idx].min, width: 300, apex: true }; // the apex: nominal band
  if (idx === TIERS.length - 2) return { low: TIERS[idx].min, width: TIERS[idx + 1].min - TIERS[idx].min };
  const span = (TIERS[idx + 1].min - TIERS[idx].min) / 3;
  const sub = Math.max(0, Math.min(2, Math.floor((value - TIERS[idx].min) / span)));
  if (idx === 0 && sub === 0) return { low: LADDER_FLOOR, width: TIERS[0].min + span - LADDER_FLOOR }; // Bronze I: from the floor
  return { low: TIERS[idx].min + span * sub, width: span };
}
